/**
 * PP.Cart — Theme-wide cart module for PersonaPath
 *
 * Single source of truth for cart state on any page. Tracks
 * handle → {lineKey, variantId, sellingPlanId} so removals always
 * use the precise line key returned by /cart/add.js, not a variant ID
 * lookup that can match the wrong line when subscription + one-time
 * lines coexist.
 *
 * Events fired on document:
 *   pp:cart:changed  { action: 'added'|'removed'|'synced', handle, cart, openDrawer?, lineKey? }
 *
 * Drawer suppression: per-call via window.__ppSkipDrawer.
 * The flag is set synchronously before the fetch and cleared in finally,
 * so the drawer interceptor's .then() sees it before our await resumes.
 */

window.PP = window.PP || {};

(function () {
  'use strict';

  var SHOPIFY_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var STORAGE_KEY = 'pp_cart_v2';

  // Map<handle, {lineKey: string|null, variantId: number|null, sellingPlanId: number|null}>
  var _items = new Map();

  // ── Persistence ──────────────────────────────────────────────────────────

  function _load() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return;
      arr.forEach(function (entry) {
        if (entry && entry.handle) {
          _items.set(entry.handle, {
            lineKey: entry.lineKey || null,
            variantId: entry.variantId || null,
            sellingPlanId: entry.sellingPlanId || null
          });
        }
      });
    } catch (e) {}
  }

  function _save() {
    try {
      var arr = [];
      _items.forEach(function (val, handle) {
        arr.push({ handle: handle, lineKey: val.lineKey, variantId: val.variantId, sellingPlanId: val.sellingPlanId });
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch (e) {}
  }

  // ── Events ───────────────────────────────────────────────────────────────

  function _emit(action, handle, cart, extra) {
    document.dispatchEvent(new CustomEvent('pp:cart:changed', {
      bubbles: true,
      detail: Object.assign({ action: action, handle: handle, cart: cart }, extra || {})
    }));
  }

  // ── Public API ───────────────────────────────────────────────────────────

  /**
   * Add a product variant to the cart.
   *
   * @param {string}      handle
   * @param {number}      variantId
   * @param {object}     [opts]
   * @param {number|null} [opts.sellingPlanId]
   * @param {boolean}    [opts.openDrawer=true]  pass false to suppress the cart drawer
   * @returns {Promise<{cart: object, lineKey: string|null}>}
   */
  async function add(handle, variantId, opts) {
    opts = opts || {};
    var sellingPlanId = opts.sellingPlanId || null;
    var openDrawer = opts.openDrawer !== false;

    var item = { id: variantId, quantity: 1 };
    if (sellingPlanId) item.selling_plan = sellingPlanId;

    // Set per-call drawer suppression BEFORE the fetch.
    // The drawer interceptor's .then() runs in the same microtask queue tick
    // as the fetch response — before our await resumes — so the flag is still
    // true when the interceptor decides whether to open the drawer.
    window.__ppSkipDrawer = !openDrawer;

    var cart;
    try {
      // Phase 2a: stamp the quiz session ID as a cart attribute before adding.
      // Defined in personapath-results.js; no-op on pages where it isn't loaded.
      if (typeof window.ppAttachQuizSessionToCart === 'function') {
        await window.ppAttachQuizSessionToCart();
      }

      var addRes = await fetch(SHOPIFY_ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      });

      if (!addRes.ok) {
        var errBody = await addRes.json().catch(function () { return {}; });
        throw new Error(errBody.description || 'Cart add failed (' + addRes.status + ')');
      }

      var addData = await addRes.json();
      // /cart/add.js returns the added line items — capture the key immediately
      var lineKey = (addData.items && addData.items[0]) ? addData.items[0].key : null;

      // Fetch updated cart for item_count and authoritative line state
      var cartRes = await fetch(SHOPIFY_ROOT + 'cart.js');
      cart = await cartRes.json();

      _items.set(handle, { lineKey: lineKey, variantId: variantId, sellingPlanId: sellingPlanId });
      _save();

      _emit('added', handle, cart, { openDrawer: openDrawer, lineKey: lineKey });
      return { cart: cart, lineKey: lineKey };

    } finally {
      // Always clear — even on throw — so a failed add never permanently
      // suppresses the drawer for subsequent operations.
      window.__ppSkipDrawer = false;
    }
  }

  /**
   * Remove a product from the cart by handle.
   *
   * Uses the stored line key from add() when available. If not found,
   * falls back to a live cart lookup matching variant + selling plan to
   * avoid accidentally removing a different line for the same variant
   * (e.g., subscription vs. one-time coexisting in the cart).
   *
   * @param {string}      handle
   * @param {number|null} [variantId]   used for fallback lookup only
   * @returns {Promise<object>}  updated cart
   */
  async function remove(handle, variantId) {
    var stored = _items.get(handle);
    var lineKey = stored ? stored.lineKey : null;
    var vId = variantId || (stored && stored.variantId) || null;

    if (!lineKey && vId) {
      var liveRes = await fetch(SHOPIFY_ROOT + 'cart.js');
      var liveCart = await liveRes.json();
      var spId = stored ? stored.sellingPlanId : null;
      var lineItem = (liveCart.items || []).find(function (i) {
        if (i.variant_id !== vId) return false;
        if (spId) return !!(i.selling_plan_allocation && i.selling_plan_allocation.selling_plan.id === spId);
        return true;
      });
      lineKey = lineItem ? lineItem.key : null;
    }

    if (!lineKey) {
      // Line not found — stale state. Clean up silently.
      _items.delete(handle);
      _save();
      var fallbackCart = await fetch(SHOPIFY_ROOT + 'cart.js').then(function (r) { return r.json(); });
      _emit('removed', handle, fallbackCart, {});
      return fallbackCart;
    }

    var res = await fetch(SHOPIFY_ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
    if (!res.ok) throw new Error('Cart remove failed (' + res.status + ')');

    var updatedCart = await res.json();
    _items.delete(handle);
    _save();
    _emit('removed', handle, updatedCart, {});
    return updatedCart;
  }

  /**
   * Sync local state against the live Shopify cart.
   *
   * Only removes handles that are confirmed absent AND whose variantId
   * is known. Handles without variantId info are left untouched — this
   * prevents a partially-loaded page from wiping valid state when product
   * data hasn't been fetched yet.
   *
   * @returns {Promise<object|null>}  live cart, or null on network failure
   */
  async function sync() {
    try {
      var res = await fetch(SHOPIFY_ROOT + 'cart.js');
      if (!res.ok) return null;
      var cart = await res.json();

      var liveByVariant = new Map();
      (cart.items || []).forEach(function (item) {
        liveByVariant.set(item.variant_id, item.key);
      });

      var changed = false;
      _items.forEach(function (val, handle) {
        if (!val.variantId) return;
        if (!liveByVariant.has(val.variantId)) {
          _items.delete(handle);
          changed = true;
        } else {
          var freshKey = liveByVariant.get(val.variantId);
          if (freshKey !== val.lineKey) {
            val.lineKey = freshKey;
            changed = true;
          }
        }
      });

      if (changed) _save();
      _emit('synced', null, cart, {});
      return cart;
    } catch (e) {
      return null;
    }
  }

  function has(handle) {
    return _items.has(handle);
  }

  function getEntry(handle) {
    return _items.get(handle) || null;
  }

  // ── Init ─────────────────────────────────────────────────────────────────

  _load();

  // Cross-tab sync: another tab modified our cart storage key
  window.addEventListener('storage', function (e) {
    if (e.key !== STORAGE_KEY) return;
    _items.clear();
    _load();
    _emit('synced', null, null, { source: 'cross-tab' });
  });

  window.PP.Cart = { add: add, remove: remove, sync: sync, has: has, getEntry: getEntry };
})();
